import React from 'react';
import {
  Box,
  Text,
  Flex,
  Button,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Spinner,
  Textarea,
  useToast,
  TableContainer,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
} from '@chakra-ui/react';
import { ChevronDownIcon } from '@chakra-ui/icons';
import qs from 'qs';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import 'dayjs/locale/es';
import xhr from 'xhr';
import useAuth from 'hooks/useAuth';
import BasicModal from 'components/modal/Modal';

dayjs.extend(relativeTime);
dayjs.locale('es');

async function fetchSchoolSubjectsWithAskCounts(schoolId) {
  // 1. Get class-groups for school to find program IDs
  const cgQuery = qs.stringify(
    {
      filters: { school: { id: { $eq: schoolId } } },
      pagination: { limit: -1 },
      fields: ['id'],
      populate: {
        grade: { fields: ['id'] },
        technical_career: { fields: ['id'] },
        course: { fields: ['id'] },
      },
    },
    { encodeValuesOnly: true }
  );
  const cgRes = await xhr(`/class-groups?${cgQuery}`);
  const classGroups = cgRes?.data?.data ?? cgRes?.data ?? [];

  const programFilters = [];
  classGroups.forEach((cg) => {
    const attrs = cg.attributes || cg;
    const grade = attrs.grade?.data || attrs.grade;
    const tc = attrs.technical_career?.data || attrs.technical_career;
    const course = attrs.course?.data || attrs.course;
    if (grade?.id) programFilters.push({ grade: { id: { $eq: grade.id } } });
    if (tc?.id) programFilters.push({ technical_career: { id: { $eq: tc.id } } });
    if (course?.id) programFilters.push({ course: { id: { $eq: course.id } } });
  });

  if (programFilters.length === 0) return { subjects: [], totalCount: 0 };

  // 2. Get subjects for those programs
  const subjQuery = qs.stringify(
    {
      filters: { $or: programFilters },
      pagination: { limit: -1 },
      fields: ['name'],
      populate: {
        course: { fields: ['name'] },
        grade: { fields: ['name'] },
        technical_career: { fields: ['name'] },
      },
    },
    { encodeValuesOnly: true }
  );
  const subjRes = await xhr(`/subjects?${subjQuery}`);
  const subjectList = subjRes?.data?.data ?? subjRes?.data ?? [];

  if (subjectList.length === 0) return { subjects: [], totalCount: 0 };

  // 3. Get pending asks counts per subject
  const subjectIds = subjectList.map((s) => s.id);
  const askQuery = qs.stringify(
    {
      filters: {
        subject: { id: { $in: subjectIds } },
        answered: { $null: true },
      },
      pagination: { limit: -1 },
      fields: ['id'],
      populate: { subject: { fields: ['id'] } },
    },
    { encodeValuesOnly: true }
  );
  const askRes = await xhr(`/asks?${askQuery}`);
  const asks = askRes?.data?.data ?? askRes?.data ?? [];

  const countMap = {};
  let totalCount = 0;
  asks.forEach((a) => {
    const subjId = a.subject?.id;
    if (!subjId) return;
    totalCount += 1;
    countMap[subjId] = (countMap[subjId] || 0) + 1;
  });

  const subjects = subjectList
    .map((s) => ({ ...s, pendingCount: countMap[s.id] || 0 }))
    .sort((a, b) => b.pendingCount - a.pendingCount);

  return { subjects, totalCount };
}

async function fetchAsksForSubject(subjectId) {
  const query = qs.stringify(
    {
      filters: {
        subject: { id: { $eq: subjectId } },
        answered: { $null: true },
      },
      pagination: { limit: -1 },
      sort: ['createdAt:desc'],
      populate: ['user', 'subject', 'material'],
    },
    { encodeValuesOnly: true }
  );
  const res = await xhr(`/asks?${query}`);
  const list = res?.data?.data ?? res?.data ?? [];
  return Array.isArray(list) ? list : [];
}

async function fetchAskDetail(askId) {
  const askRes = await xhr(`/asks/${askId}?populate=deep,3`);
  const ask = askRes?.data?.data ?? askRes?.data;

  const repliesQuery = qs.stringify(
    {
      filters: { ask: { id: { $eq: ask.id } } },
      populate: ['user'],
      pagination: { limit: -1 },
      sort: ['createdAt:asc'],
    },
    { encodeValuesOnly: true }
  );
  const repliesRes = await xhr(`/ask-replies?${repliesQuery}`);
  const replies = repliesRes?.data?.data ?? repliesRes?.data ?? [];

  return { ask, replies: Array.isArray(replies) ? replies : [] };
}

export default function SchoolPreguntas() {
  const { user } = useAuth();
  const toast = useToast();

  const [subjects, setSubjects] = React.useState([]);
  const [totalCount, setTotalCount] = React.useState(0);
  const [selectedSubject, setSelectedSubject] = React.useState(null);
  const [asks, setAsks] = React.useState([]);
  const [loadingSubjects, setLoadingSubjects] = React.useState(true);
  const [loadingAsks, setLoadingAsks] = React.useState(false);

  // Detail modal state
  const [isOpen, setIsOpen] = React.useState(false);
  const [currentAsk, setCurrentAsk] = React.useState(null);
  const [replies, setReplies] = React.useState([]);
  const [replyText, setReplyText] = React.useState('');
  const [loadingDetail, setLoadingDetail] = React.useState(false);
  const [saving, setSaving] = React.useState(false);

  // 1. Load subjects
  React.useEffect(() => {
    const load = async () => {
      try {
        const schoolId = user?.school?.id;
        if (!schoolId) return;
        const { subjects: list, totalCount: total } =
          await fetchSchoolSubjectsWithAskCounts(schoolId);
        setSubjects(list);
        setTotalCount(total);
        if (list.length > 0 && list[0].pendingCount > 0) {
          setSelectedSubject(list[0]);
        }
      } catch (error) {
        console.error(error);
      } finally {
        setLoadingSubjects(false);
      }
    };
    load();
  }, [user?.school?.id]);

  // 2. Load asks when subject changes
  React.useEffect(() => {
    if (!selectedSubject) {
      setAsks([]);
      return;
    }
    let cancelled = false;
    const load = async () => {
      setLoadingAsks(true);
      try {
        const data = await fetchAsksForSubject(selectedSubject.id);
        if (!cancelled) setAsks(data);
      } catch (error) {
        console.error(error);
        if (!cancelled) setAsks([]);
      } finally {
        if (!cancelled) setLoadingAsks(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [selectedSubject?.id]);

  const handleSelectSubject = (subj) => setSelectedSubject(subj);

  const handleOpenAsk = async (ask) => {
    setIsOpen(true);
    setCurrentAsk(ask);
    setReplies([]);
    setReplyText('');
    setLoadingDetail(true);
    try {
      const { ask: fullAsk, replies: reps } = await fetchAskDetail(ask.id);
      setCurrentAsk(fullAsk);
      setReplies(reps);
    } catch (error) {
      console.error(error);
    } finally {
      setLoadingDetail(false);
    }
  };

  const handleSendReply = async () => {
    try {
      if (!replyText.trim()) return alert('Debes escribir una respuesta');
      setSaving(true);

      const res = await xhr.post('/ask-replies', {
        data: {
          content: replyText,
          user: user.id,
          ask: currentAsk.id,
          material: currentAsk.material?.id || null,
        },
      });

      const newReply = res?.data?.data ?? res?.data;
      setReplies((prev) => [...prev, { ...newReply, user }]);
      setReplyText('');

      toast({
        title: 'Respuesta enviada correctamente',
        status: 'success',
        duration: 2000,
        isClosable: true,
      });
    } catch (error) {
      console.error(error);
    } finally {
      setSaving(false);
    }
  };

  const handleMarkAnswered = async () => {
    try {
      setSaving(true);
      await xhr.put(`/asks/${currentAsk.id}`, {
        data: { answered: true },
      });

      // Remove from list and update counts
      setAsks((prev) => prev.filter((a) => a.id !== currentAsk.id));
      setSubjects((prev) =>
        prev
          .map((s) => {
            if (s.id !== selectedSubject?.id) return s;
            return { ...s, pendingCount: Math.max(0, s.pendingCount - 1) };
          })
          .sort((a, b) => b.pendingCount - a.pendingCount)
      );
      setSelectedSubject((prev) =>
        prev ? { ...prev, pendingCount: Math.max(0, prev.pendingCount - 1) } : null
      );
      setTotalCount((prev) => Math.max(0, prev - 1));

      setIsOpen(false);
      setCurrentAsk(null);
      setReplies([]);
      setReplyText('');

      toast({
        title: 'Pregunta marcada como respondida',
        status: 'success',
        duration: 2000,
        isClosable: true,
      });
    } catch (error) {
      console.error(error);
    } finally {
      setSaving(false);
    }
  };

  if (loadingSubjects) {
    return (
      <Flex justify="center" mt={10}>
        <Spinner size="xl" />
      </Flex>
    );
  }

  return (
    <Box>
      <Flex align="center" gap={2} mb={4}>
        <Text fontSize="28px" fontWeight="bold">
          Preguntas por responder
        </Text>
        <Text fontSize="28px" fontWeight="bold" color="red.500">
          ({totalCount})
        </Text>
      </Flex>

      {/* Subject filter */}
      <Flex align="center" mb={5}>
        <Menu>
          <MenuButton as={Button} rightIcon={<ChevronDownIcon />}>
            <Flex align="center">
              {selectedSubject ? (
                <>
                  {selectedSubject.name}
                  {selectedSubject.pendingCount > 0 && (
                    <Text color="red.500" ml={2}>
                      [{selectedSubject.pendingCount}]
                    </Text>
                  )}
                </>
              ) : (
                'Seleccionar materia'
              )}
            </Flex>
          </MenuButton>
          <MenuList maxH="300px" overflowY="auto">
            {subjects.map((subj) => (
              <MenuItem
                key={subj.id}
                onClick={() => handleSelectSubject(subj)}
                fontWeight={subj.id === selectedSubject?.id ? 'bold' : 'normal'}
              >
                <Flex>
                  {subj.name}
                  {subj.pendingCount > 0 && (
                    <Text color="red.500" ml={2}>
                      [{subj.pendingCount}]
                    </Text>
                  )}
                </Flex>
              </MenuItem>
            ))}
          </MenuList>
        </Menu>
      </Flex>

      {/* Content */}
      {!selectedSubject ? (
        <Text color="gray.500">Selecciona una materia para ver las preguntas pendientes.</Text>
      ) : loadingAsks ? (
        <Flex justify="center" mt={6}>
          <Spinner size="lg" />
        </Flex>
      ) : asks.length === 0 ? (
        <Text color="gray.500">No hay preguntas pendientes en esta materia.</Text>
      ) : (
        <TableContainer>
          <Table>
            <Thead bg="gray.200">
              <Tr>
                <Th>Estudiante</Th>
                <Th>Materia</Th>
                <Th>Asunto</Th>
                <Th>Fecha</Th>
                <Th></Th>
              </Tr>
            </Thead>
            <Tbody>
              {asks.map((ask) => {
                const diffDate = dayjs().diff(ask.createdAt, 'day');
                return (
                  <Tr key={ask.id} color={diffDate > 1 ? 'red.500' : undefined}>
                    <Td>{ask.user?.first_name} {ask.user?.last_name}</Td>
                    <Td>{ask.subject?.name}</Td>
                    <Td>{ask.matter}</Td>
                    <Td>{dayjs(ask.createdAt).fromNow()}</Td>
                    <Td>
                      <Button size="sm" colorScheme="purple" onClick={() => handleOpenAsk(ask)}>
                        Ver
                      </Button>
                    </Td>
                  </Tr>
                );
              })}
            </Tbody>
          </Table>
        </TableContainer>
      )}

      {/* Detail modal */}
      <BasicModal isOpen={isOpen} setIsOpen={setIsOpen} size="3xl">
        {currentAsk && (
          <Box>
            {loadingDetail ? (
              <Flex justify="center" py={10}>
                <Spinner size="lg" />
              </Flex>
            ) : (
              <>
                {/* Original question */}
                <Box mb={4} p={4} bg="gray.50" borderRadius="md">
                  <Flex justify="space-between" align="center" mb={2}>
                    <Text fontWeight="bold" fontSize="lg">
                      Mensaje de {currentAsk.user?.first_name} {currentAsk.user?.last_name}
                    </Text>
                    <Text fontSize="xs" color="gray.500">
                      {dayjs(currentAsk.createdAt).fromNow()}
                    </Text>
                  </Flex>
                  {currentAsk.matter && (
                    <Text fontSize="sm" color="gray.600" mb={2}>
                      Asunto: {currentAsk.matter}
                    </Text>
                  )}
                  <Text>{currentAsk.content}</Text>
                </Box>

                {/* Replies thread */}
                {replies.length > 0 && (
                  <Box mb={4}>
                    {replies.map((reply) => {
                      const isSchoolAdmin = reply.user?.id === user.id;
                      return (
                        <Box
                          key={reply.id}
                          ml={isSchoolAdmin ? '40px' : '0'}
                          mb={3}
                          p={3}
                          bg={isSchoolAdmin ? 'purple.50' : 'white'}
                          border="1px solid"
                          borderColor={isSchoolAdmin ? 'purple.200' : 'gray.200'}
                          borderRadius="md"
                        >
                          <Flex justify="space-between" align="center" mb={1}>
                            <Text fontWeight="bold" fontSize="sm">
                              {isSchoolAdmin ? 'Tú' : reply.user?.first_name}
                            </Text>
                            <Text fontSize="xs" color="gray.500">
                              {dayjs(reply.createdAt).fromNow()}
                            </Text>
                          </Flex>
                          <Text fontSize="sm">{reply.content}</Text>
                        </Box>
                      );
                    })}
                  </Box>
                )}

                {/* Reply form */}
                <Box>
                  <Text mb={1} fontWeight="medium">
                    Responder
                  </Text>
                  <Textarea
                    value={replyText}
                    onChange={(e) => setReplyText(e.target.value)}
                    placeholder="Escribe tu respuesta..."
                    rows={3}
                    mb={3}
                  />
                  <Flex gap={3}>
                    <Button
                      colorScheme="purple"
                      bg="#574feb"
                      onClick={handleSendReply}
                      isLoading={saving}
                    >
                      Enviar respuesta
                    </Button>
                    <Button
                      colorScheme="red"
                      variant="outline"
                      onClick={handleMarkAnswered}
                      isLoading={saving}
                    >
                      Marcar como respondida
                    </Button>
                  </Flex>
                </Box>
              </>
            )}
          </Box>
        )}
      </BasicModal>
    </Box>
  );
}
